/**
 * Tarama basligindaki "Piyasa Modu" karti.
 *
 * SORU: su an piyasa dusus mu yukselis mi agirlikli?
 *
 * DURUSTLUK SINIRI - BU BIR TAHMIN DEGILDIR
 *   Buradaki hicbir sayi gelecegi ongormez. Hepsi SU ANIN olcumudur.
 *   Bu oturumdaki arastirmalar (bkz. research/REPORT_discovery2.md,
 *   REPORT_sinyalx_mtf_kombinasyon.md) halka acik mum verisinden yon
 *   ONGORUSU uretilemedigini tekrar tekrar gosterdi - o yuzden burada
 *   "yukselecek/dusecek" demiyoruz, "su an ne agirlikta" diyoruz.
 *
 *   "1 saatte" karsilastirmasi da bir tahmin degil: gecmis iki olcumun
 *   farkidir. Yeterli gecmis yoksa (sunucu yeni basladi) HIC gosterilmez.
 *
 * VERI
 *   - Genislik: taranan evrende 24s'te yukselen / dusen sembol sayisi
 *     (backend /api/market/overview, bellekteki evrenden - yeni istek yok)
 *   - Sinyal dengesi: SECILI periyottaki onayli Yukselis / Dusus sayisi
 *     (zaten ekranda olan tarama sonucundan - ek istek yok)
 */

import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { MarketOverview, SignalScan } from "../api/types";
import { IconTrendDown, IconTrendUp, Pill } from "./Ui";

const REFRESH_MS = 60_000;

/** Bu farkin altinda taraflar DENGELI sayilir - gurultuyu "mod" ilan etmeyiz. */
const BALANCED_BAND_PCT = 6;
/** Bu kadar puanlik degisim "yon degisiyor" demek icin yeterli. */
const SHIFT_THRESHOLD_PCT = 3;

type Mode = "down" | "up" | "flat";

function modeOf(advancingPct: number): Mode {
  if (advancingPct < 50 - BALANCED_BAND_PCT / 2) return "down";
  if (advancingPct > 50 + BALANCED_BAND_PCT / 2) return "up";
  return "flat";
}

const MODE_LABEL: Record<Mode, string> = {
  down: "DÜŞÜŞ AĞIRLIKLI",
  up: "YÜKSELİŞ AĞIRLIKLI",
  flat: "DENGELİ",
};

export function MarketModeBanner({ scan }: { scan: SignalScan | null }) {
  const [overview, setOverview] = useState<MarketOverview | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await api.marketOverview();
        if (!cancelled) setOverview(payload);
      } catch {
        /* yardimci bilgi - basarisiz olursa kart gizlenir, taramayi bloklamaz */
      }
    };
    void load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const total = overview ? overview.advancing + overview.declining : 0;
  if (!overview || total === 0 || overview.advancing_pct == null) return null;

  const advancingPct = overview.advancing_pct;
  const mode = modeOf(advancingPct);
  const earlier = overview.advancing_pct_1h_ago;
  const shift = earlier != null ? advancingPct - earlier : null;

  const signalTotal = scan ? scan.buy_count + scan.sell_count : 0;
  const buyPct = signalTotal > 0 && scan ? (scan.buy_count / signalTotal) * 100 : null;

  return (
    <div className="market-mode" data-mode={mode}>
      <div className="market-mode-head">
        <span className="market-mode-icon">
          {mode === "down" ? <IconTrendDown /> : <IconTrendUp />}
        </span>
        <span className="market-mode-title">Piyasa Modu</span>
        <Pill tone={mode === "down" ? "down" : mode === "up" ? "up" : "neutral"}>
          {MODE_LABEL[mode]}
        </Pill>
        {shift != null && Math.abs(shift) >= SHIFT_THRESHOLD_PCT ? (
          <Pill
            tone={shift > 0 ? "up" : "down"}
            title={`1 saat önce yükselenlerin oranı %${earlier?.toFixed(0)} idi`}
          >
            {shift > 0 ? "toparlanıyor" : "zayıflıyor"}
          </Pill>
        ) : null}
      </div>

      {/* Tek bakista oran: yesil kisim yukselen, kirmizi kisim dusen. */}
      <div
        className="market-mode-bar"
        role="img"
        aria-label={`Taranan ${total} coinin %${advancingPct.toFixed(0)}'i yükselişte`}
      >
        <span className="market-mode-up" style={{ width: `${advancingPct}%` }} />
      </div>

      <div className="market-mode-stats">
        <span>
          <strong className="up">{overview.advancing}</strong> yükselen ·{" "}
          <strong className="down">{overview.declining}</strong> düşen
          <span className="note"> (24s, {total} coin)</span>
        </span>
        {buyPct != null && scan ? (
          <span>
            Sinyaller: <strong className="up">{scan.buy_count}</strong> yükseliş ·{" "}
            <strong className="down">{scan.sell_count}</strong> düşüş
            <span className="note"> ({scan.timeframe.toUpperCase()})</span>
          </span>
        ) : null}
        {shift != null ? (
          <span className="note">
            1 saat önce %{earlier?.toFixed(0)} → şimdi %{advancingPct.toFixed(0)}
          </span>
        ) : (
          <span className="note">Değişim için yeterli geçmiş yok</span>
        )}
      </div>

      <p className="note market-mode-foot">
        Bu bir <strong>tahmin değil</strong>, şu anın ölçümü: piyasanın yönünü önceden
        bilemiyoruz (bkz. araştırma raporları). Düşüş ağırlıklı bir piyasada yükseliş
        sinyalleri daha sık boşa çıkar.
      </p>
    </div>
  );
}
